import { useKeycloak } from "@react-keycloak/web";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as MiaLogo } from "../../assets/images/brand/mia-completo-logo.svg";
import codertdLogo from "../../assets/images/brand/codertd-logo.png";
import "./login.css";

const APP_VERSION =
  (typeof process !== "undefined" && process.env?.REACT_APP_VERSION) || "1.2.1";

const Login: FunctionComponent = () => {
  const { keycloak } = useKeycloak();
  const { t } = useTranslation();

  const onEnterClick = (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    keycloak.login();
  };

  const onRegisterClick = (evt: React.MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    keycloak.register();
  };

  return (
    <div className="mr-login-page">
      <div className="mr-login-bg-decor" />

      <header className="mr-login-topbar">
        <a
          href="https://www.miareputation.com"
          className="mr-login-topbar-brand"
          aria-label="Mia Reputation"
        >
          <MiaLogo className="mr-login-topbar-logo" />
        </a>
        <a
          href="https://www.miareputation.com"
          className="mr-login-topbar-back"
        >
          ← {t("login.back_to_site")}
        </a>
      </header>

      <div className="mr-login-card">
        <div className="mr-login-intro">
          <span className="mr-login-eyebrow">{t("login.eyebrow")}</span>
          <h1 className="mr-login-title">{t("login.welcome_back")}</h1>
          <p className="mr-login-subtitle">{t("login.subtitle")}</p>
        </div>

        <div className="mr-login-quick-stats">
          <div className="mr-login-stat">
            <strong>300+</strong>
            <small>{t("login.stat_practices")}</small>
          </div>
          <div className="mr-login-stat">
            <strong>4h</strong>
            <small>{t("login.stat_sla")}</small>
          </div>
          <div className="mr-login-stat">
            <strong>92%</strong>
            <small>{t("login.stat_success")}</small>
          </div>
        </div>

        <button
          type="button"
          onClick={onEnterClick}
          className="mr-login-btn-primary"
        >
          {t("login.enter")}
        </button>

        <p className="mr-login-register-link">
          {t("login.no_account")}
          <a href="#" onClick={onRegisterClick}>
            {t("login.register_now")}
          </a>
        </p>

        <div className="mr-login-divider" />

        <div className="mr-login-trust-badges">
          <div className="mr-login-badge">
            <span className="mr-login-badge-icon">🔒</span>
            <span>
              {t("login.badge_gdpr_title")}
              <br />
              <small>{t("login.badge_gdpr_sub")}</small>
            </span>
          </div>
          <div className="mr-login-badge">
            <span className="mr-login-badge-icon">📋</span>
            <span>
              {t("login.badge_iso_title")}
              <br />
              <small>{t("login.badge_iso_sub")}</small>
            </span>
          </div>
          <div className="mr-login-badge">
            <span className="mr-login-badge-icon">🏛</span>
            <span>
              {t("login.badge_garante_title")}
              <br />
              <small>{t("login.badge_garante_sub")}</small>
            </span>
          </div>
          <div className="mr-login-badge">
            <span className="mr-login-badge-icon">🇪🇺</span>
            <span>
              {t("login.badge_eu_title")}
              <br />
              <small>{t("login.badge_eu_sub")}</small>
            </span>
          </div>
        </div>

        <div className="mr-login-affiliations">
          <span className="mr-login-aff-item">
            <strong>Startup innovativa</strong> · MISE
          </span>
          <span className="mr-login-aff-sep">·</span>
          <span className="mr-login-aff-item">
            Incubata <strong>I3P</strong> Politecnico di Torino
          </span>
        </div>
      </div>

      <footer className="mr-login-footer">
        <div className="mr-login-footer-left">
          <div className="mr-login-system-status">
            <span className="mr-login-status-dot" />
            {t("login.status_operational")}
          </div>
          <span className="mr-login-help">
            <a href="mailto:support@miareputation.com">
              support@miareputation.com
            </a>
          </span>
        </div>
        <div className="mr-login-footer-right">
          <span>{t("login.powered_by")}</span>
          <img
            src={codertdLogo}
            alt="CoDe RTD"
            className="mr-login-codertd-logo"
          />
          <span className="mr-login-sep">·</span>
          <a
            href="https://www.iubenda.com/privacy-policy/14774579/full-legal"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy
          </a>
          <span className="mr-login-sep">·</span>
          <a
            href="https://www.miareputation.com/tos"
            target="_blank"
            rel="noopener noreferrer"
          >
            Termini
          </a>
          <span className="mr-login-sep">·</span>
          <span className="mr-login-version">v{APP_VERSION}</span>
        </div>
      </footer>
    </div>
  );
};

export default Login;
